import { Bell } from "lucide-react";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { asPercent } from "@/lib/format";
import { useContrarianSignals, useSignalsSummary } from "@/sdk/hooks/use-signals";

export function SentinelPanel() {
  const summary = useSignalsSummary();
  const opportunities = useContrarianSignals(5);

  if (summary.isLoading || opportunities.isLoading) {
    return <LoadingState text="Loading sentinel signals..." />;
  }

  const topSignals = opportunities.data?.opportunities ?? [];

  return (
    <Panel title="Sentinel">
      {/* Each query fails independently: a summary error must not fabricate
          "0 total signals", and an opportunities error must not read as a
          calm empty feed. */}
      {summary.isError ? (
        <QueryErrorState
          what="sentinel summary"
          onRetry={() => void summary.refetch()}
          className="mb-3"
        />
      ) : (
        <div className="mb-3 flex items-center gap-2 text-xs text-white/50">
          <Bell className="h-4 w-4" />
          <span>
            {summary.data?.total_signals ?? 0} total signals · {summary.data?.contrarian_count ?? 0} contrarian
          </span>
        </div>
      )}
      {opportunities.isError ? (
        <QueryErrorState
          what="sentinel opportunities"
          onRetry={() => void opportunities.refetch()}
        />
      ) : topSignals.length === 0 ? (
        <p className="py-4 text-center text-sm text-white/40">No active sentinel opportunities.</p>
      ) : (
        <div className="space-y-1.5">
          {topSignals.map((signal) => (
            <div key={`${signal.market_id}-${signal.token_id}`} className="rounded border border-white/10 px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-white/80">{signal.signal}</span>
                <span className="text-xs text-white/30">{asPercent(signal.confidence)}</span>
              </div>
              <p className="mt-0.5 text-xs text-white/70">{signal.question ?? signal.market_id}</p>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
